import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://prompthub-api.socialvalue.de/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'https://prompthub-api.socialvalue.de/api'}/auth/refresh`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        const newToken = response.data.token;
        localStorage.setItem('token', newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        // Token refresh failed, redirect to login
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    if (error.response?.data?.error) {
      toast.error(error.response.data.error);
    } else if (error.message) {
      toast.error(error.message);
    }

    return Promise.reject(error);
  }
);

// API methods
export const promptsAPI = {
  // Get all prompts
  getPrompts: async (params = {}) => {
    const response = await api.get('/prompts', { params });
    return response.data;
  },
  
  // Get single prompt
  getPrompt: async (id) => {
    const response = await api.get(`/prompts/${id}`);
    return response.data;
  },
  
  // Create prompt
  createPrompt: async (data) => {
    const response = await api.post('/prompts', data);
    return response.data;
  },
  
  // Update prompt
  updatePrompt: async (id, data) => {
    const response = await api.put(`/prompts/${id}`, data);
    return response.data;
  },
  
  // Delete prompt
  deletePrompt: async (id) => {
    const response = await api.delete(`/prompts/${id}`);
    return response.data;
  },
  
  // Get prompt versions
  getPromptVersions: async (id) => {
    const response = await api.get(`/prompts/${id}/versions`);
    return response.data;
  },
  
  // Add comment to prompt
  addComment: async (id, content) => {
    const response = await api.post(`/prompts/${id}/comment`, { content });
    return response.data;
  },
  
  // Copy prompt to clipboard
  copyPrompt: async (id) => {
    const response = await api.post(`/prompts/${id}/copy`);
    return response.data;
  },
  
  // Like/unlike prompt
  likePrompt: async (id) => {
    const response = await api.post(`/prompts/${id}/like`);
    return response.data;
  },
  
  // Get categories
  getCategories: async () => {
    const response = await api.get('/prompts/categories');
    return response.data.data; // Directly return the array of categories
  },
  
  // Get prompt statistics
  getStats: async () => {
    const response = await api.get('/prompts/stats');
    return response.data;
  },
};

export const tagsAPI = {
  // Get all tags
  getTags: async (params = {}) => {
    const response = await api.get('/tags', { params });
    return response.data.data; // Correctly return the array of tags
  },
  
  // Get single tag
  getTag: async (id) => {
    const response = await api.get(`/tags/${id}`);
    return response.data;
  },
  
  // Create tag
  createTag: async (data) => {
    const response = await api.post('/tags', data);
    return response.data;
  },
  
  // Update tag
  updateTag: async (id, data) => {
    const response = await api.put(`/tags/${id}`, data);
    return response.data;
  },
  
  // Delete tag
  deleteTag: async (id) => {
    const response = await api.delete(`/tags/${id}`);
    return response.data;
  },
  
  // Get popular tags
  getPopularTags: async (limit = 10) => {
    const response = await api.get('/tags/popular', { params: { limit } });
    return response.data.data; // Assuming this also returns { data: [...] }
  },
  
  // Get tag categories
  getTagCategories: async () => {
    const response = await api.get('/tags/categories');
    return response.data.data; // Assuming this also returns { data: [...] }
  },
};

export const authAPI = {
  // Verify token
  verifyToken: () => api.get('/auth/verify'),
  
  // Refresh token
  refreshToken: () => api.post('/auth/refresh'),
  
  // Logout
  logout: () => api.post('/auth/logout'),
  
  // Get profile
  getProfile: () => api.get('/auth/profile'),
  
  // Update profile
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const usersAPI = {
  // Get all users (admin only)
  getUsers: (params = {}) => api.get('/users', { params }),
  
  // Get single user (admin only)
  getUser: (id) => api.get(`/users/${id}`),
  
  // Update user role (admin only)
  updateUserRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  
  // Update user status (admin only)
  updateUserStatus: (id, isActive) => api.put(`/users/${id}/status`, { isActive }),
  
  // Get user stats (admin only)
  getUserStats: () => api.get('/users/stats'),
};

export const integrationsAPI = {
  // Google Drive
  getGoogleDriveFiles: (params = {}) => api.get('/integrations/google-drive/files', { params }),
  linkGoogleDriveFile: (promptId, fileId) => api.post('/integrations/google-drive/link', { promptId, fileId }),
  
  // Confluence
  getConfluenceSpaces: () => api.get('/integrations/confluence/spaces'),
  getConfluencePages: (params = {}) => api.get('/integrations/confluence/pages', { params }),
  linkConfluencePage: (promptId, pageId) => api.post('/integrations/confluence/link', { promptId, pageId }),
  
  // Integration status
  getIntegrationStatus: () => api.get('/integrations/status'),
};

export default api;
